import React from 'react';
import { useIntl } from 'react-intl';
import { ProposalAIRichContent } from './ProposalAIRichContent';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';
import {
  ProposalAIGeneratorServiceCatalogSelect,
  ProposalAIGeneratorUnitCatalogSelect,
} from './ProposalAIGeneratorCatalogSelect';
import {
  ProposalAIGeneratorApplyMode,
  ProposalAIGeneratorServicePreviewState,
} from '../hooks';
import { CatalogPreviewOption, groupGeneratedBlocks } from '../utils';
import { Service } from 'modules/services';
import { Unit } from 'modules/units';

interface Props {
  generated: GenerateProposalBlocksPayload;
  currentProposalName: string;
  generatedProposalNameSuggestion: string;
  currentSegmentsLength: number;
  isGeneratedStale: boolean;
  servicePreviewStates: ProposalAIGeneratorServicePreviewState[];
  onApplyGenerated: (
    mode: ProposalAIGeneratorApplyMode,
  ) => React.MouseEventHandler<HTMLButtonElement>;
  onServicePreviewSelectionChange: (
    blockIndex: number,
    field: 'service' | 'unit',
    option: CatalogPreviewOption<Service> | CatalogPreviewOption<Unit> | null,
  ) => void;
  onUseSuggestedName: React.MouseEventHandler<HTMLButtonElement>;
}

export const ProposalAIGeneratorPreviewSection: React.FC<Props> = ({
  generated,
  currentProposalName,
  generatedProposalNameSuggestion,
  currentSegmentsLength,
  isGeneratedStale,
  servicePreviewStates,
  onApplyGenerated,
  onServicePreviewSelectionChange,
  onUseSuggestedName,
}) => {
  const { formatMessage } = useIntl();

  function getServicePreviewState(blockIndex: number) {
    return (
      servicePreviewStates.find((previewState) => previewState.blockIndex === blockIndex) ||
      null
    );
  }

  return (
    <div className="proposal-ai-generator__section">
      <div className="proposal-ai-generator__section-heading">
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.preview.description' })}
        </p>
      </div>

      {generatedProposalNameSuggestion &&
        generatedProposalNameSuggestion !== currentProposalName && (
          <div className="proposal-ai-generator__name-suggestion">
            <div className="proposal-ai-generator__name-suggestion-text">
              <p className="t-label t-text-3 m-bottom--tny">
                {formatMessage({ id: 'proposal-ai.preview.name-suggestion.label' })}
              </p>
              <p className="t-epsilon m-bottom-0">
                {generatedProposalNameSuggestion}
              </p>
            </div>
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.preview.use-name' })}
              onClick={onUseSuggestedName}
              style="secondary"
              size="med"
              btnSelector="proposal-ai-use-suggested-name"
            />
          </div>
        )}

      {isGeneratedStale && (
        <div className="proposal-ai-generator__notice proposal-ai-generator__notice--warning">
          <p className="t-text-2 m-bottom-0">
            {formatMessage({ id: 'proposal-ai.preview.stale' })}
          </p>
        </div>
      )}

      {generated.unresolvedMappings.length > 0 && (
        <div className="proposal-ai-generator__notice proposal-ai-generator__notice--info">
          <p className="t-label m-bottom--tny">
            {formatMessage({ id: 'proposal-ai.unresolved.title' })}
          </p>
          <p className="t-text-2 m-bottom--tny">
            {formatMessage({ id: 'proposal-ai.unresolved.description' })}
          </p>
          <p className="t-text-2 m-bottom--tny">
            {formatMessage({ id: 'proposal-ai.unresolved.preview-action' })}
          </p>
          <ul className="proposal-ai-generator__warning-list">
            {generated.unresolvedMappings.map((mapping) => (
              <li
                key={`${mapping.blockIndex}-${mapping.field}-${mapping.suggestedValue}`}
                className="t-text-2"
              >
                {formatMessage(
                  { id: 'proposal-ai.unresolved.item' },
                  {
                    blockNumber: mapping.blockIndex + 1,
                    field: mapping.field,
                    suggestedValue: mapping.suggestedValue,
                  },
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="proposal-ai-generator__preview-list">
        {groupGeneratedBlocks(generated.blocks).map((group, groupIndex) => (
          <div
            className="proposal-ai-generator__preview-group"
            key={`group-${groupIndex}`}
          >
            {group.heading && (
              <article className="proposal-ai-generator__preview-item">
                <div className="proposal-ai-generator__preview-type">
                  heading
                </div>
                <h4 className="t-delta m-top--tny m-bottom--tny">
                  {group.heading.block.title}
                </h4>
                {group.heading.block.subtitle && (
                  <p className="t-text-2 m-bottom-0">
                    {group.heading.block.subtitle}
                  </p>
                )}
              </article>
            )}

            {!!group.items.length && (
              <div className="proposal-ai-generator__preview-group-rail">
                {group.items.map(({ block, index }) => (
                  <article
                    className="proposal-ai-generator__preview-item"
                    key={`${block.type}-${index}`}
                  >
                    <div className="proposal-ai-generator__preview-type">
                      {block.type}
                    </div>

                    {block.type === 'text' && (
                      <ProposalAIRichContent content={block.description} />
                    )}

                    {block.type === 'service' && (
                      <>
                        {(() => {
                          const previewState = getServicePreviewState(index);

                          if (!previewState || !previewState.selection) {
                            return null;
                          }

                          return (
                            <div className="proposal-ai-generator__catalog-grid">
                              <ProposalAIGeneratorServiceCatalogSelect
                                blockIndex={index}
                                options={previewState.serviceOptions}
                                value={previewState.selection.service}
                                onChange={(selectedOption) =>
                                  onServicePreviewSelectionChange(
                                    index,
                                    'service',
                                    selectedOption,
                                  )
                                }
                              />
                              <ProposalAIGeneratorUnitCatalogSelect
                                blockIndex={index}
                                options={previewState.unitOptions}
                                value={previewState.selection.unit}
                                onChange={(selectedOption) =>
                                  onServicePreviewSelectionChange(
                                    index,
                                    'unit',
                                    selectedOption,
                                  )
                                }
                              />
                            </div>
                          );
                        })()}
                        <ProposalAIRichContent content={block.description} />
                      </>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="proposal-ai-generator__action-row">
        {currentSegmentsLength > 0 ? (
          <>
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.apply.append' })}
              onClick={onApplyGenerated('append')}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-apply-append"
            />
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.apply.replace' })}
              onClick={onApplyGenerated('replace')}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-apply-replace"
              style="destructive__outline"
              icon="destructive"
            />
          </>
        ) : (
          <ProposalAIPrimaryButton
            label={formatMessage({ id: 'proposal-ai.apply.create' })}
            onClick={onApplyGenerated('replace')}
            disabled={isGeneratedStale}
            btnSelector="proposal-ai-apply"
          />
        )}
      </div>
    </div>
  );
};
